import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

export async function fetchCapacity() {
  const { data } = await api.get('/api/capacity');
  return data;
}

export async function createPreregistration(payload) {
  const { data } = await api.post('/api/preregistrations', payload);
  return data;
}

export async function fetchPreregistrationSummary(id) {
  const { data } = await api.get(`/api/preregistrations/${id}`);
  return data;
}

/** `POST /api/preregistrations/:id/payment-intent` — prépare un paiement Stripe. */
export async function createPaymentIntent(id) {
  const { data } = await api.post(`/api/preregistrations/${id}/payment-intent`);
  return data;
}

/**
 * `POST /api/preregistrations/:id/attend` — l'utilisateur confirme sa présence
 * en cliquant sur le bouton du mail. Idempotent côté serveur.
 */
export async function confirmAttendance(id) {
  const { data } = await api.post(`/api/preregistrations/${id}/attend`);
  return data;
}

/**
 * `PATCH /api/preregistrations/:id` — modification self-service (augmentation only).
 * Renvoie le summary à jour ou 409/422 si la deadline est dépassée / diminution.
 */
export async function publicUpdateCounts(id, { adults, children }) {
  const { data } = await api.patch(`/api/preregistrations/${id}`, { adults, children });
  return data;
}

/**
 * `DELETE /api/preregistrations/:id` — annulation self-service.
 * Toujours autorisée tant que la résa n'est pas déjà annulée. Le front
 * affiche un avertissement avant l'appel si `paid_cents > 0`.
 */
export async function publicCancelPreregistration(id) {
  const { data } = await api.delete(`/api/preregistrations/${id}`);
  return data;
}

export async function lookupPreregistration(email) {
  const { data } = await api.post('/api/preregistrations/lookup', { email });
  return data;
}

export async function requestRecoveryLink(email) {
  const { data } = await api.post('/api/preregistrations/lookup/send-link', { email });
  return data;
}

/** Accepte un jeton admin string ou un objet d'en-têtes (session bénévole). */
function toAdminHeaders(auth) {
  if (auth && typeof auth === 'object') return auth;
  if (typeof auth === 'string' && auth.trim()) return { 'X-Admin-Token': auth.trim() };
  return {};
}

export async function fetchAdminList(auth) {
  const { data } = await api.get('/api/admin/preregistrations', {
    headers: toAdminHeaders(auth),
  });
  return data;
}

/**
 * Crée une préréservation depuis l’admin (appel téléphonique).
 * `email` null / omis → email technique unique, pas d’envoi SMTP.
 */
export async function createPhonePreregistration(auth, payload) {
  const { data } = await api.post('/api/admin/preregistrations', payload, {
    headers: toAdminHeaders(auth),
  });
  return data;
}

export async function cancelPreregistration(auth, id) {
  const { data } = await api.delete(`/api/admin/preregistrations/${id}`, {
    headers: toAdminHeaders(auth),
  });
  return data;
}

/**
 * Suppression **définitive** d'une préréservation annulée (admin uniquement).
 * Côté API la résa doit déjà être en état `cancelled` — sinon 400.
 * Les tickets liés sont supprimés en cascade.
 */
export async function hardDeletePreregistration(auth, id) {
  await api.delete(`/api/admin/preregistrations/${id}/permanent`, {
    headers: toAdminHeaders(auth),
  });
}

/**
 * Restaure une préréservation précédemment annulée (admin uniquement).
 * Côté API : `cancelled_at = NULL` ; si la résa était confirmée, ses tickets
 * annulés sont également réactivés. Si un autre user a recréé une résa active
 * avec le même email entre-temps, le backend renvoie 409 (`conflict`).
 */
export async function restorePreregistration(auth, id) {
  const { data } = await api.post(`/api/admin/preregistrations/${id}/restore`, null, {
    headers: toAdminHeaders(auth),
  });
  return data;
}

export async function updatePreregistrationCounts(auth, id, { adults, children }) {
  const { data } = await api.patch(
    `/api/admin/preregistrations/${id}`,
    { adults, children },
    { headers: toAdminHeaders(auth) },
  );
  return data;
}

export async function fetchPricing(auth) {
  const { data } = await api.get('/api/admin/pricing', {
    headers: toAdminHeaders(auth),
  });
  return data;
}

export async function updatePricing(auth, {
  adultEur,
  childEur,
  musselsPortionGrams,
  sausagesPortionGrams,
  friesPortionGrams,
  mealCapacity,
}) {
  const { data } = await api.put(
    '/api/admin/pricing',
    {
      adult_eur: adultEur,
      child_eur: childEur,
      mussels_portion_grams: musselsPortionGrams,
      sausages_portion_grams: sausagesPortionGrams,
      fries_portion_grams: friesPortionGrams,
      meal_capacity: mealCapacity,
    },
    { headers: toAdminHeaders(auth) },
  );
  return data;
}

export async function updatePayment(auth, id, { paidEur, paymentMethod, paymentNotes }) {
  const { data } = await api.patch(
    `/api/admin/preregistrations/${id}/payment`,
    {
      paid_eur: paidEur,
      payment_method: paymentMethod || null,
      payment_notes: paymentNotes || null,
    },
    { headers: toAdminHeaders(auth) },
  );
  return data;
}

export async function sendOneReminder(auth, id) {
  const { data } = await api.post(
    `/api/admin/preregistrations/${id}/reminder`,
    {},
    { headers: toAdminHeaders(auth) },
  );
  return data;
}

export async function sendBulkReminders(auth, target) {
  const { data } = await api.post(
    '/api/admin/reminders/bulk',
    { target },
    { headers: toAdminHeaders(auth), timeout: 120000 },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Tickets nominatifs (1 par convive)
// ---------------------------------------------------------------------------

export async function fetchReservationTickets(reservationId) {
  const { data } = await api.get(`/api/preregistrations/${reservationId}/tickets`);
  return data;
}

export async function updateTicketMainChoice(ticketId, mainChoice) {
  const { data } = await api.patch(`/api/tickets/${ticketId}/main-choice`, {
    main_choice: mainChoice,
  });
  return data;
}

export async function updateTicketLabel(ticketId, label) {
  const { data } = await api.patch(`/api/tickets/${ticketId}/label`, {
    guest_label: label || null,
  });
  return data;
}

// --- Scanner bénévole (jeton stocké dans localStorage) ---------------------

const VOL_HEADER = 'X-Volunteer-Token';

export async function scanLookupTicket(token, idOrCode) {
  const { data } = await api.get(`/api/scan/tickets/${encodeURIComponent(idOrCode)}`, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

export async function scanBuffetPass(token, ticketId) {
  const { data } = await api.post(`/api/scan/tickets/${ticketId}/buffet`, {}, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

/** +1 portion moules / saucisses / frites (`product` = mussels | sausages | fries). */
export async function scanRecordBuffetProduct(token, ticketId, product) {
  const { data } = await api.post(
    `/api/scan/tickets/${ticketId}/buffet-product`,
    { product },
    { headers: { [VOL_HEADER]: token } },
  );
  return data;
}

/** Annule le dernier scan de ce produit pour ce ticket. */
export async function scanUndoBuffetProduct(token, ticketId, product) {
  const { data } = await api.post(
    `/api/scan/tickets/${ticketId}/buffet-product/undo`,
    { product },
    { headers: { [VOL_HEADER]: token } },
  );
  return data;
}

export async function scanBuffetUndo(token, ticketId) {
  const { data } = await api.post(`/api/scan/tickets/${ticketId}/buffet/undo`, {}, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

export async function scanDessertServe(token, ticketId) {
  const { data } = await api.post(`/api/scan/tickets/${ticketId}/dessert`, {}, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

export async function scanDessertUndo(token, ticketId) {
  const { data } = await api.post(`/api/scan/tickets/${ticketId}/dessert/undo`, {}, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

/** Bon apéros (1 QR par réservation) — lookup pour le poste bar. */
export async function scanLookupAperitifVoucher(token, idOrCode) {
  const { data } = await api.get(`/api/scan/aperitif-vouchers/${encodeURIComponent(idOrCode)}`, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

export async function scanAperitifVoucherRedeem(token, voucherId) {
  const { data } = await api.post(`/api/scan/aperitif-vouchers/${voucherId}/redeem`, {}, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

export async function scanAperitifVoucherRedeemUndo(token, voucherId) {
  const { data } = await api.post(`/api/scan/aperitif-vouchers/${voucherId}/redeem/undo`, {}, {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

/** Totaux agrégés scans buffet (pour écran cuisine / projection). */
export async function fetchBuffetConsumption(token) {
  const { data } = await api.get('/api/scan/buffet-consumption', {
    headers: { [VOL_HEADER]: token },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Rôle « saisie » (caisse / accueil le jour J)
// ---------------------------------------------------------------------------

const SAISIE_HEADER = 'X-Saisie-Token';

/** Accepte un jeton string ou un objet d'en-têtes (session bénévole). */
function toSaisieHeaders(auth) {
  if (auth && typeof auth === 'object') return auth;
  if (typeof auth === 'string' && auth.trim()) return { [SAISIE_HEADER]: auth.trim() };
  return {};
}

/**
 * Demande au backend à quel rôle correspond un jeton. Utilisé par /admin pour
 * rediriger un bénévole qui saisirait son code scan ou saisie sur la mauvaise
 * page. Lève une erreur 401 si le jeton n'est reconnu pour aucun rôle.
 *
 * @param {string} token
 * @returns {Promise<'admin'|'scan'|'saisie'>}
 */
export async function probeRole(token) {
  const { data } = await api.post('/api/auth/probe', { token });
  return data.role;
}

export async function saisieSearch(auth, query) {
  const { data } = await api.get('/api/saisie/preregistrations', {
    params: query ? { q: query } : {},
    headers: toSaisieHeaders(auth),
  });
  // Garde-fou : une réponse HTML (mauvais proxy / API) ne doit pas crasher la page.
  return Array.isArray(data) ? data : [];
}

export async function saisieGetOne(auth, id) {
  const { data } = await api.get(`/api/saisie/preregistrations/${id}`, {
    headers: toSaisieHeaders(auth),
  });
  return data;
}

export async function saisieUpdatePayment(auth, id, { paidEur, paymentMethod, paymentNotes }) {
  const { data } = await api.patch(
    `/api/saisie/preregistrations/${id}/payment`,
    {
      paid_eur: paidEur,
      payment_method: paymentMethod || null,
      payment_notes: paymentNotes || null,
    },
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Encaissements partiels (table `payments` cote backend)
// ---------------------------------------------------------------------------

/**
 * Historique des paiements d'une preregistration (actifs + annules), du
 * plus recent au plus ancien.
 */
export async function listPayments(auth, prereservationId) {
  const { data } = await api.get(
    `/api/saisie/preregistrations/${prereservationId}/payments`,
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

/**
 * Enregistre un nouvel encaissement (delta). Le backend calcule le montant
 * en fonction du nombre d'adultes/enfants saisi et de la grille tarifaire.
 * Renvoie la PaymentView creee.
 *
 * @param {string} token
 * @param {string} prereservationId
 * @param {{adults: number, children: number, paymentMethod: string,
 *          paymentNotes?: string, amountOverrideEur?: number}} body
 */
export async function createPayment(auth, prereservationId, body) {
  const { data } = await api.post(
    `/api/saisie/preregistrations/${prereservationId}/payments`,
    {
      adults: body.adults,
      children: body.children,
      payment_method: body.paymentMethod,
      payment_notes: body.paymentNotes || null,
      amount_override_eur:
        typeof body.amountOverrideEur === 'number' ? body.amountOverrideEur : null,
    },
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

/** Annulation (soft-delete) d'un encaissement specifique. */
export async function cancelPayment(auth, paymentId) {
  const { data } = await api.delete(`/api/saisie/payments/${paymentId}`, {
    headers: toSaisieHeaders(auth),
  });
  return data;
}

/**
 * Remet des bracelets (+delta). `adults` / `children` ≥ 0, au moins un > 0.
 * Plafond : remis + delta ≤ payé par type. Renvoie la fiche enrichie.
 */
export async function handoffBracelets(auth, prereservationId, { adults, children }) {
  const { data } = await api.post(
    `/api/saisie/preregistrations/${prereservationId}/bracelets`,
    { adults, children },
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

/**
 * Corrige le cumul bracelets (valeurs absolues, 0 ≤ valeur ≤ payé).
 */
export async function setBracelets(auth, prereservationId, { adults, children }) {
  const { data } = await api.put(
    `/api/saisie/preregistrations/${prereservationId}/bracelets`,
    { adults, children },
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

// ---------------------------------------------------------------------------
// File d'impression (backend dormant — plus d'UI)
// ---------------------------------------------------------------------------

/**
 * Tickets actifs non encore imprimes, ordonnes par date de creation.
 * Le frontend de la page /impression poll cet endpoint toutes les 5 s.
 */
export async function fetchPrintQueue(auth) {
  const { data } = await api.get('/api/saisie/print-queue', {
    headers: toSaisieHeaders(auth),
  });
  return data;
}

/**
 * Lookup d'un lot precis de tickets par UUIDs (max 64 par appel). Utilise
 * par la page d'impression batch pour afficher les 8 tickets selectionnes
 * (meme s'ils ont deja ete marques imprimes).
 */
export async function fetchTicketsByIds(auth, ticketIds) {
  if (!ticketIds || ticketIds.length === 0) return [];
  const { data } = await api.get('/api/saisie/tickets', {
    params: { ids: ticketIds.join(',') },
    headers: toSaisieHeaders(auth),
  });
  return data;
}

/**
 * Marque un lot de tickets comme imprimes. Idempotent : ignore ceux deja
 * imprimes ou annules. Renvoie le nombre de tickets reellement mis a jour.
 */
export async function markTicketsPrinted(auth, ticketIds) {
  const { data } = await api.patch(
    '/api/saisie/tickets/print',
    { ticket_ids: ticketIds },
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

/** Reinitialise le statut imprime d'un lot (cas papier coince). */
export async function markTicketsUnprinted(auth, ticketIds) {
  const { data } = await api.patch(
    '/api/saisie/tickets/unprint',
    { ticket_ids: ticketIds },
    { headers: toSaisieHeaders(auth) },
  );
  return data;
}

export function buildCsvUrl() {
  return `${BASE_URL}/api/admin/preregistrations.csv`;
}

export function buildCashflowCsvUrl(method) {
  if (!method || method === 'all') {
    return `${BASE_URL}/api/admin/cashflow.csv`;
  }
  return `${BASE_URL}/api/admin/cashflow.csv?method=${encodeURIComponent(method)}`;
}

export function extractApiError(err, fallback = 'Erreur inattendue') {
  if (err?.response?.data?.message) return err.response.data.message;
  if (err?.message) return err.message;
  return fallback;
}

// ---------------------------------------------------------------------------
// Bénévoles (registre, lien magique, planning)
// ---------------------------------------------------------------------------

const VOL_SESSION_HEADER = 'X-Volunteer-Session';

export async function registerVolunteer(payload) {
  const { data } = await api.post('/api/volunteers/register', payload);
  return data;
}

export async function requestVolunteerLink(email) {
  const { data } = await api.post('/api/volunteers/request-link', { email });
  return data;
}

export async function consumeVolunteerMagicLink(token) {
  const { data } = await api.post('/api/volunteers/magic-link/consume', { token });
  return data;
}

export async function fetchVolunteerMe(sessionToken) {
  const { data } = await api.get('/api/volunteer/me', {
    headers: { [VOL_SESSION_HEADER]: sessionToken },
  });
  return data;
}

export async function fetchVolunteerShifts(sessionToken) {
  const { data } = await api.get('/api/volunteer/shifts', {
    headers: { [VOL_SESSION_HEADER]: sessionToken },
  });
  return data;
}

export async function signupVolunteerShift(sessionToken, shiftId) {
  const { data } = await api.post(
    `/api/volunteer/shifts/${shiftId}/signup`,
    {},
    { headers: { [VOL_SESSION_HEADER]: sessionToken } },
  );
  return data;
}

export async function unsignupVolunteerShift(sessionToken, shiftId) {
  const { data } = await api.delete(`/api/volunteer/shifts/${shiftId}/signup`, {
    headers: { [VOL_SESSION_HEADER]: sessionToken },
  });
  return data;
}

function adminOrSessionHeaders(headers) {
  return headers && Object.keys(headers).length ? headers : {};
}

export async function fetchAdminVolunteers(headers) {
  const { data } = await api.get('/api/admin/volunteers', {
    headers: adminOrSessionHeaders(headers),
  });
  return data;
}

/** Création admin (email optionnel). `activate: true` → active sans attendre. */
export async function createAdminVolunteer(headers, payload) {
  const { data } = await api.post(
    '/api/admin/volunteers',
    {
      last_name: payload.last_name,
      first_name: payload.first_name,
      email: payload.email || null,
      phone: payload.phone || null,
      activate: Boolean(payload.activate),
    },
    { headers: adminOrSessionHeaders(headers) },
  );
  return data;
}

export async function adminAssignShiftSignup(headers, shiftId, volunteerId) {
  const { data } = await api.post(
    `/api/admin/volunteers/shifts/${shiftId}/signups/${volunteerId}`,
    {},
    { headers: adminOrSessionHeaders(headers) },
  );
  return data;
}

export async function approveVolunteer(headers, id) {
  const { data } = await api.post(`/api/admin/volunteers/${id}/approve`, {}, {
    headers: adminOrSessionHeaders(headers),
  });
  return data;
}

export async function deleteOrRejectVolunteer(headers, id) {
  const { data } = await api.delete(`/api/admin/volunteers/${id}`, {
    headers: adminOrSessionHeaders(headers),
  });
  return data;
}

export async function sendVolunteerLink(headers, id) {
  const { data } = await api.post(`/api/admin/volunteers/${id}/send-link`, {}, {
    headers: adminOrSessionHeaders(headers),
  });
  return data;
}

export async function renewVolunteer(headers, id, days) {
  const { data } = await api.post(
    `/api/admin/volunteers/${id}/renew`,
    days != null ? { days } : {},
    { headers: adminOrSessionHeaders(headers) },
  );
  return data;
}

export async function renewAllVolunteers(headers, days) {
  const { data } = await api.post(
    '/api/admin/volunteers/renew-all',
    days != null ? { days } : {},
    { headers: adminOrSessionHeaders(headers) },
  );
  return data;
}

export async function setVolunteerStaffAccess(headers, id, role) {
  const { data } = await api.post(
    `/api/admin/volunteers/${id}/staff-access`,
    { role },
    { headers: adminOrSessionHeaders(headers) },
  );
  return data;
}

export async function clearVolunteerStaffAccess(headers, id) {
  const { data } = await api.delete(`/api/admin/volunteers/${id}/staff-access`, {
    headers: adminOrSessionHeaders(headers),
  });
  return data;
}

export async function fetchVolunteerConflicts(headers) {
  const { data } = await api.get('/api/admin/volunteers/conflicts', {
    headers: adminOrSessionHeaders(headers),
  });
  return data;
}

export function buildVolunteerPlanningCsvUrl() {
  return `${BASE_URL}/api/admin/volunteers/planning.csv`;
}

export async function adminRemoveShiftSignup(headers, shiftId, volunteerId) {
  const { data } = await api.delete(
    `/api/admin/volunteers/shifts/${shiftId}/signups/${volunteerId}`,
    { headers: adminOrSessionHeaders(headers) },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Mémorisation locale de la dernière préréservation effectuée depuis ce
// navigateur. Sert à reconnaître le client à son retour et à lui proposer
// un accès direct à son récap (sans qu'il ait à passer par `/retrouver`).
//
// On stocke seulement l'email, le prénom et l'UUID de préréservation —
// suffisant pour afficher « Bonjour X » et linker vers `/payer/:uuid`.
// ---------------------------------------------------------------------------

const REMEMBERED_KEY = 'sqseverac.lastPreregistration';

/**
 * Mémorise (ou met à jour) la préréservation associée à ce navigateur.
 *
 * Accepte un objet partiel `{ id?, email, first_name?, last_name?, meals? }`.
 * Seul `email` est obligatoire — `id` est facultatif car la route publique
 * `/api/preregistrations/lookup` ne renvoie volontairement pas l'UUID (sécurité :
 * empêcher un tiers d'accéder au détail financier en devinant un email).
 *
 * Si une entrée existait déjà pour le **même email**, on **préserve** les champs
 * non fournis (notamment l'UUID), pour qu'une recherche ultérieure via `/retrouver`
 * ne « downgrade » pas une mémorisation initialement créée par le formulaire.
 */
export function rememberReservation(reg) {
  if (!reg?.email) return;
  try {
    const existing = getRememberedReservation();
    const sameEmail = existing && existing.email === reg.email;
    const toStore = {
      id: reg.id ?? (sameEmail ? existing.id : null),
      email: reg.email,
      first_name: reg.first_name ?? (sameEmail ? existing.first_name : ''),
      last_name: reg.last_name ?? (sameEmail ? existing.last_name : ''),
      meals: reg.meals ?? (sameEmail ? existing.meals : null),
      saved_at: new Date().toISOString(),
    };
    localStorage.setItem(REMEMBERED_KEY, JSON.stringify(toStore));
  } catch {
    // localStorage indisponible (mode privé Safari par exemple) : on ignore.
  }
}

export function getRememberedReservation() {
  try {
    const raw = localStorage.getItem(REMEMBERED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Seul email est obligatoire ; id peut être null (cas où la mémorisation
    // vient d'une recherche par email sans accès au détail).
    if (!parsed?.email) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function forgetReservation() {
  try {
    localStorage.removeItem(REMEMBERED_KEY);
  } catch {
    // idem
  }
}
